import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Edit,
  Trash2,
  Pin,
  ArrowUp,
  Building2,
} from "lucide-react";

const VAT_RATE = 0.25;

export const ProductCard = ({ 
  product, 
  categories, 
  onEdit, 
  onDelete, 
  onPin, 
  onUnpin,
  getDiscountedPrice 
}) => {
  const discountedPrice = getDiscountedPrice(product);
  const priceExVat = product.price_ex_vat || (product.price / (1 + VAT_RATE));
  const isPinned = product.pinned && (!product.pinned_until || new Date(product.pinned_until) > new Date());
  
  return (
    <div
      className={`bg-white/5 rounded-lg border overflow-hidden hover:border-[#23F7DD]/50 transition-all cursor-pointer group relative ${
        isPinned ? 'border-yellow-500/50 ring-2 ring-yellow-500/30' : 'border-white/10'
      }`}
    >
      <div className="aspect-square bg-black/50 relative overflow-hidden" onClick={() => onEdit(product)}>
        {product.photos && product.photos.length > 0 && product.photos[0] ? (
          <img
            src={product.photos[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full flex flex-col items-center justify-center ${product.photos && product.photos.length > 0 && product.photos[0] ? 'hidden' : ''}`}>
          <Package className="w-16 h-16 text-[#475569]" />
          <span className="text-xs text-[#64748B] mt-2">No Image</span>
        </div>
        {/* Pinned badge */}
        {isPinned && (
          <Badge className="absolute top-2 left-2 bg-yellow-500 text-black font-bold">
            <Pin className="w-3 h-3 mr-1" /> TOP
          </Badge>
        )}
        {/* Discount badge */}
        {product.discount_enabled && !isPinned && (
          <Badge className="absolute top-2 left-2 bg-red-500 text-white">
            -{product.discount_percent}%
          </Badge>
        )}
        {/* Discount badge when pinned */}
        {product.discount_enabled && isPinned && (
          <Badge className="absolute top-10 left-2 bg-red-500 text-white">
            -{product.discount_percent}%
          </Badge>
        )}
        {/* Low stock badge */}
        {(product.stock_quantity || 0) < 5 && (
          <Badge className="absolute top-2 right-2 bg-orange-500">
            Low Stock
          </Badge>
        )}
        {/* Edit overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Edit className="w-8 h-8 text-white" />
        </div>
      </div>
      
      {/* Pin to Top Button */}
      <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
        {isPinned ? (
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onUnpin(product); }}
            className="bg-yellow-500 text-black hover:bg-yellow-600 shadow-lg"
          >
            <Pin className="w-3 h-3 mr-1" /> Unpin
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onPin(product); }}
            className="bg-white/90 text-black hover:bg-white shadow-lg"
          >
            <ArrowUp className="w-3 h-3 mr-1" /> Pin Top
          </Button>
        )}
      </div>
      
      <div className="p-4" onClick={() => onEdit(product)}>
        <div className="mb-2">
          <Badge variant="outline" className="text-xs border-white/20 text-[#94A3B8] mb-1">
            {categories.find(c => c.id === product.category)?.icon} {product.category?.replace("_", " ")}
          </Badge>
          <h4 className="text-white font-medium line-clamp-2">{product.name}</h4>
        </div>
        
        {/* Pricing with VAT */}
        <div className="space-y-1">
          {discountedPrice ? (
            <div className="flex items-baseline gap-2">
              <span className="text-[#23F7DD] font-bold text-lg">{discountedPrice.toFixed(0)} NOK</span>
              <span className="text-red-400 line-through text-sm">{product.price} NOK</span>
            </div>
          ) : (
            <p className="text-[#23F7DD] font-bold text-lg">{product.price} NOK</p>
          )}
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <Building2 className="w-3 h-3" />
            <span>Ex. VAT: {priceExVat.toFixed(0)} NOK</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
          <span className="text-sm text-[#94A3B8]">Stock: {product.stock_quantity || 0}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => onDelete(product.product_id, e)}
            className="text-[#94A3B8] hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
